"use client"
import { useEffect, useRef, useState } from "react"
import useSWR from "swr"
import { RotateCcw, Square } from "lucide-react"
import { Button } from "@/components/ui/animated/button/button"
import { api } from "@/lib/api-client"
import type { AiRunSummary } from "@/modules/ai/evidence-contracts"
import type { AiRunProgress } from "@/modules/chatbot/lifecycle-contracts"
import { communicationActions as actions } from "./state"
import { useConversationSpanish } from "./use-public-conversation"
import { AiMessage } from "./ai-message"
import { communicationActorSurface } from "./actor-surface"
import styles from "./ai-experience.module.css"

export function AiReplyProgress({ threadId, actor, cacheScope, run, active = true, showPartial = true, assistantName = "DanAI" }: { threadId: string; actor: string; cacheScope?: string; run?: AiRunSummary; active?: boolean; showPartial?: boolean; assistantName?: string }) {
  const es = useConversationSpanish(), refreshed = useRef("")
  const [busy, setBusy] = useState(false), [error, setError] = useState("")
  const surface = communicationActorSurface(actor)
  const endpoint = run ? `/api/communications/${threadId}/ai?surface=${surface}&runId=${run.id}` : null
  const available = active && run && !["completed", "discarded"].includes(run.state)
  const progress = useSWR<AiRunProgress>(available ? [cacheScope, actor, endpoint] : null, ([,,url]: [string | undefined,string,string]) => api(url), {
    refreshInterval: data => !data || ["pending", "running"].includes(data.state) ? 1000 : 0,
    dedupingInterval: 700, revalidateOnFocus: true, shouldRetryOnError: false,
  })
  const current = progress.data?.id === run?.id ? progress.data : undefined
  const state = current?.state ?? run?.state
  useEffect(() => {
    if (!current || ["pending", "running"].includes(current.state) || refreshed.current === current.id) return
    refreshed.current = current.id
    void actions.reloadThread(threadId).catch(() => {})
  }, [current, threadId])
  if (!available) return null
  async function change(action: "stop" | "retry") {
    if (!run || busy) return
    setBusy(true); setError("")
    try { await actions.controlAiRun(threadId, run.id, action); await progress.mutate() }
    catch (issue) { setError(issue instanceof Error ? issue.message : es ? "No se pudo actualizar la respuesta." : "The reply could not be updated.") }
    finally { setBusy(false) }
  }
  return <section className={styles.progress} aria-label={es ? `Respuesta de ${assistantName}` : `${assistantName} response`} aria-busy={state === "running" || state === "pending"}>
    {showPartial && current?.text && ["pending", "running", "cancelled"].includes(current.state) && <div className={styles.partial}><AiMessage text={current.text}/></div>}
    {state === "cancelled" && <small role="status">{es ? "Respuesta detenida. El texto de arriba está incompleto." : "Reply stopped. Any text above is incomplete."}</small>}
    {current?.error && <p role="alert">{current.error}</p>}
    <div className={styles.buttons}>
      {(current?.canStop ?? (state === "pending" || state === "running")) && <Button size="sm" variant="ghost" disabled={busy} onClick={() => void change("stop")}><Square size={12}/>{es ? "Detener respuesta" : "Stop reply"}</Button>}
      {(current?.canRetry ?? (state === "failed" || state === "cancelled")) && <Button size="sm" variant="secondary" disabled={busy} onClick={() => void change("retry")}><RotateCcw size={14}/>{es ? "Volver a intentar" : "Try reply again"}</Button>}
      {progress.error && <Button size="sm" variant="ghost" disabled={progress.isValidating} onClick={() => void progress.mutate().catch(() => {})}>{es ? "Actualizar respuesta" : "Refresh reply"}</Button>}
    </div>
    {(error || progress.error) && <small role="alert" className={styles.error}>{error || (es ? "No se pudo actualizar la respuesta. Tu mensaje enviado sigue guardado." : "The reply could not refresh. Your sent message is still saved.")}</small>}
  </section>
}
